import { useCallback, useState } from 'react';
import api from '../utils/api.util';
import toast from '../utils/toast.util';
import { ENDPOINT } from '../constants/common-app.const';
import { displayDateToIso } from '../pages/CourseEnrollment/CourseEnrollment.utils';

const CLIENT_TYPE_MAP: Record<string, string> = {
  legal: 'legal_entity',
  ip: 'sole_proprietor',
  individual: 'individual',
};

export interface CreateContractPayload {
  courseId: string;
  groupId: string | null;
  consentGiven: boolean;
  clientType: string;
  manuallyEntered: boolean;
  vkods?: string | null;
  form: {
    unp: string;
    organization: string;
    legalAddress: string;
    iban: string;
    bankName: string;
    bic: string;
    postalAddress: string;
    postalDiffers: boolean;
    phone: string;
    email: string;
    signerName: string;
    signerPosition: string;
    signingBasis: string;
    funding: string;
    passportNumber: string;
    passportIssueDate: string;
    passportIssuer: string;
    individualFullName: string;
  };
}

/**
 * Единый маппинг формы шага 1 в client-объект API. Используется и при создании
 * договора (POST), и при повторном проходе шага 1 у уже созданного черновика
 * (PATCH) — иначе правки шага 1 (в т.ч. IBAN/банк) молча терялись.
 */
export const buildClientPayload = (payload: CreateContractPayload) => ({
  clientType: CLIENT_TYPE_MAP[payload.clientType] ?? payload.clientType,
  manuallyEntered: payload.manuallyEntered,
  vkods: payload.vkods || null,
  vnaimp: payload.form.organization || null,
  unp: payload.form.unp || null,
  iban: payload.form.iban.trim() || null,
  bankName: payload.form.bankName.trim() || null,
  bic: payload.form.bic.trim() || null,
  legalAddress: payload.form.legalAddress || null,
  postalAddress: payload.form.postalAddress || null,
  postalDiffers: payload.form.postalDiffers,
  contactPhone: payload.form.phone || null,
  contactEmail: payload.form.email || null,
  signatoryFio: payload.form.signerName || null,
  signatoryPosition: payload.form.signerPosition || null,
  signatoryBasis: payload.form.signingBasis || null,
  fioRu: payload.form.individualFullName || null,
  fioBy: null,
  passportNumber: payload.form.passportNumber || null,
  passportIssueDate: displayDateToIso(payload.form.passportIssueDate),
  passportIssuedBy: payload.form.passportIssuer || null,
  financingSource: payload.form.funding || null,
});

export const buildCreateContractBody = (payload: CreateContractPayload) => ({
  courseId: payload.courseId,
  groupId: payload.groupId || null,
  consentGiven: payload.consentGiven,
  client: buildClientPayload(payload),
});

/**
 * Тело PATCH при повторном проходе шага 1. Отличается от создания одним: vkods
 * приходит только из ответа ИМНС и в форме не живёт, поэтому пустое значение
 * означает «не перезапрашивали», а не «очистить» — такой ключ не отправляем,
 * иначе возврат на шаг 1 затирал бы уже сохранённый код.
 */
export const buildClientUpdatePayload = (payload: CreateContractPayload) => {
  const { vkods, ...rest } = buildClientPayload(payload);
  return vkods ? { ...rest, vkods } : rest;
};

const useCreateContract = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createContract = useCallback(async (payload: CreateContractPayload): Promise<{ contractId: string; contractUuid: string }> => {
    try {
      setLoading(true);
      const body = buildCreateContractBody(payload);
      const response = await api.postJson(ENDPOINT.CONTRACTS, body);
      setLoading(false);
      return { contractId: response.contract.id, contractUuid: response.contract.uuid };
    } catch (err: any) {
      const status = err?.statusCode ?? err?.status;
      if (status === 422) {
        toast.showOnFailure('Этот тип клиента недоступен для выбранного курса');
      } else {
        toast.showOnFailure('Ошибка создания договора');
      }
      setError('create failed');
      setLoading(false);
      throw err;
    }
  }, []);

  return { createContract, loading, error };
};

export default useCreateContract;
